import TokenAdapter from '../../services/TokenAdapter';
import {
  BATTLE_ROOM_VISUAL_STATE_KEYS,
  extractSnapshotEntityReference,
  normalizeSnapshotEntityType,
  normalizeSnapshotLayerId,
  pickDefinedEntries
} from './BattleRoomSnapshotContract';

const cloneValue = (value) => {
  if (value === undefined) return undefined;
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (_) {
    return value;
  }
};

const getNormalizedEntityType = (token, mergedEntityData) => (
  normalizeSnapshotEntityType(token?.type || mergedEntityData?.type || 'character')
);

export const getSessionTemplateLookupCandidates = (token, embeddedData) => {
  const entityRef = extractSnapshotEntityReference(token);
  const rawCandidates = [
    entityRef.entityId,
    entityRef.canonicalId,
    token?.characterDataRef,
    token?.monsterDataRef,
    // Historical local BattleRoom tokens used monsterId as their only stable
    // built-in reference. It is an explicit identity, never a name heuristic.
    // date_of_change: 2026-09-06
    token?.monsterId,
    token?.baseTemplate,
    embeddedData?.baseTemplate,
    embeddedData?.id,
    token?.entity?.id,
    token?.originalUnifiedEntity?.id,
    embeddedData?.originalUnifiedEntity?.id
  ].filter(Boolean);

  const candidates = [];
  rawCandidates.forEach((candidate) => {
    const normalizedCandidate = String(candidate).trim();
    if (!normalizedCandidate) return;
    candidates.push(normalizedCandidate);
    candidates.push(normalizedCandidate.replace(/^(character|monster)_/i, ''));
  });

  return [...new Set(candidates.filter(Boolean))];
};

export const mergeEmbeddedTokenEntityData = (embeddedData, canonicalTemplateEntity) => {
  const canonicalData = canonicalTemplateEntity?.data || null;
  const richData = embeddedData && typeof embeddedData === 'object' ? embeddedData : null;
  const wrappedEntityPayload = richData?.characterData || richData?.monsterData || null;

  if (!richData && !canonicalData && !wrappedEntityPayload) {
    return null;
  }

  const primaryData = (
    wrappedEntityPayload?.schemaData
    || wrappedEntityPayload?.data
    || wrappedEntityPayload?.canonicalData
    || wrappedEntityPayload?.originalUnifiedEntity?.data
    || wrappedEntityPayload
    || richData?.schemaData
    || richData?.data
    || richData?.canonicalData
    || richData?.originalUnifiedEntity?.data
    || canonicalData
    || null
  );

  return {
    ...(canonicalData && typeof canonicalData === 'object' ? canonicalData : {}),
    ...(richData || {}),
    ...(wrappedEntityPayload && typeof wrappedEntityPayload === 'object' ? wrappedEntityPayload : {}),
    data: primaryData || undefined,
    schemaData: wrappedEntityPayload?.schemaData || richData?.schemaData || primaryData || undefined,
    canonicalData: wrappedEntityPayload?.canonicalData || richData?.canonicalData || canonicalData || undefined,
    originalUnifiedEntity: wrappedEntityPayload?.originalUnifiedEntity || richData?.originalUnifiedEntity || canonicalTemplateEntity || null,
    compat: wrappedEntityPayload?.compat || richData?.compat || canonicalData?.compat || undefined
  };
};

const extractRuntimeOverlay = (snapshotToken) => (
  snapshotToken?.runtimeOverlay && typeof snapshotToken.runtimeOverlay === 'object'
    ? snapshotToken.runtimeOverlay
    : snapshotToken
);

const extractVisualState = (snapshotToken) => (
  snapshotToken?.visualState && typeof snapshotToken.visualState === 'object'
    ? snapshotToken.visualState
    : snapshotToken
);

const resolveRenderedGridState = (...sources) => {
  for (const source of sources) {
    const gridX = Number(source?.grid?.x);
    const gridY = Number(source?.grid?.y);
    if (Number.isFinite(gridX) && Number.isFinite(gridY)) {
      return { grid: { x: gridX, y: gridY }, gridX, gridY };
    }
  }

  for (const source of sources) {
    const gridX = Number(source?.gridX);
    const gridY = Number(source?.gridY);
    if (Number.isFinite(gridX) && Number.isFinite(gridY)) {
      return { grid: { x: gridX, y: gridY }, gridX, gridY };
    }
  }

  return { grid: null, gridX: 0, gridY: 0 };
};

export const rebuildRestoredTokenProjection = (token, mergedEntityData) => {
  if (!token || !mergedEntityData) {
    return token;
  }

  const normalizedType = getNormalizedEntityType(token, mergedEntityData);
  const runtimeOverlay = extractRuntimeOverlay(token);
  const visualState = extractVisualState(token);
  const restoredVisualState = pickDefinedEntries(visualState, BATTLE_ROOM_VISUAL_STATE_KEYS);
  const renderedGrid = resolveRenderedGridState(visualState, token, runtimeOverlay);
  const tokenLikeEntity = {
    ...mergedEntityData,
    ...cloneValue(token),
    ...cloneValue(restoredVisualState),
    // Initiative belongs to the room instance. Template derivation supplies
    // the modifier before combat. date_of_change: 2026-11-04 (Basic Rules, Initiative)
    ...(runtimeOverlay?.initiativeRoll ? { initiative: runtimeOverlay.initiativeRoll.total } : {}),
    ...cloneValue(runtimeOverlay),
    id: token.baseTemplate || mergedEntityData.id || token.id,
    name: token.name || mergedEntityData.name,
    type: normalizedType,
    layer: normalizeSnapshotLayerId(visualState?.layer || token?.layer),
    hpObj: runtimeOverlay?.hpObj ?? token.hpObj,
    hp: runtimeOverlay?.hp ?? token.hp,
    maxHp: runtimeOverlay?.maxHp ?? token.maxHp,
    ac: runtimeOverlay?.ac ?? token.ac,
    initiative: runtimeOverlay?.initiative ?? token.initiative,
    speed: runtimeOverlay?.speed ?? token.speed,
    proficiencyBonus: runtimeOverlay?.proficiencyBonus ?? token.proficiencyBonus,
    abilities: runtimeOverlay?.abilities ?? token.abilities,
    stats: runtimeOverlay?.stats ?? token.stats,
    schemaData: mergedEntityData.schemaData || mergedEntityData.data || token.schemaData,
    canonicalData: mergedEntityData.canonicalData || mergedEntityData.data || token.canonicalData,
    originalUnifiedEntity: mergedEntityData.originalUnifiedEntity || token.originalUnifiedEntity || null,
    characterData: normalizedType === 'monster' ? undefined : mergedEntityData,
    monsterData: normalizedType === 'monster' ? mergedEntityData : undefined
  };

  const restoredCore = TokenAdapter.toTokenCore(tokenLikeEntity);
  const restoredRuntimeOverlay = runtimeOverlay !== token
    ? { ...cloneValue(runtimeOverlay), id: token.id }
    : token.runtimeOverlay;
  const restoredNestedVisualState = visualState !== token
    ? { ...cloneValue(visualState), id: token.id }
    : token.visualState;

  return {
    ...cloneValue(token),
    ...cloneValue(visualState),
    ...cloneValue(runtimeOverlay),
    ...restoredCore,
    // TokenAdapter supplies template defaults for render geometry. Persisted
    // per-instance visual state must win so a resize/rotation/placement does
    // not revert when the compact token is rehydrated. date_of_change: 2026-08-25
    ...cloneValue(restoredVisualState),
    // TokenAdapter resolves mechanics from the base template and may therefore
    // project the template identifier. The saved BattleRoom instance ID is the
    // authoritative mutation/ownership identity and must survive rehydration.
    // date_of_change: 2026-08-25
    id: token.id,
    layer: normalizeSnapshotLayerId(visualState?.layer || token?.layer),
    ...(renderedGrid.grid ? { grid: renderedGrid.grid } : {}),
    gridX: renderedGrid.gridX,
    gridY: renderedGrid.gridY,
    sizeCells: visualState?.sizeCells ?? token?.sizeCells,
    // Older compact snapshots can carry a template identifier inside a nested
    // projection even though the root record retains the unique token instance
    // identifier. Normalize both nested projections at the boundary so no
    // downstream selector can revive the stale template identity.
    // date_of_change: 2026-08-25
    runtimeOverlay: restoredRuntimeOverlay,
    visualState: restoredNestedVisualState,
    entityPayload: token.entityPayload || mergedEntityData,
    characterData: restoredCore.characterData || token.characterData,
    monsterData: restoredCore.monsterData || token.monsterData
  };
};

export const rehydrateSavedToken = ({
  token,
  richEntityData = null,
  canonicalTemplateEntity = null
}) => {
  const snapshotToken = cloneValue(token || {});
  const runtimeOverlay = extractRuntimeOverlay(snapshotToken);
  const visualState = extractVisualState(snapshotToken);
  const instanceId = snapshotToken.id || visualState?.id || runtimeOverlay?.id;
  const renderedGrid = resolveRenderedGridState(visualState, snapshotToken, runtimeOverlay);
  const normalizedToken = {
    ...snapshotToken,
    ...visualState,
    ...runtimeOverlay,
    // Nested legacy projections may contain a template ID. The root snapshot
    // record is the authoritative per-instance identity and must win after all
    // compatibility fields are flattened. date_of_change: 2026-08-25
    id: instanceId,
    x: Number((visualState?.x ?? snapshotToken?.x)) || 0,
    y: Number((visualState?.y ?? snapshotToken?.y)) || 0,
    // Token.js renders nested grid first; restore must preserve that as the placement truth
    // and backfill legacy gridX/gridY for older consumers. date_of_change: 2026-06-23
    ...(renderedGrid.grid ? { grid: renderedGrid.grid } : {}),
    gridX: renderedGrid.gridX,
    gridY: renderedGrid.gridY,
    sizeCells: visualState?.sizeCells ?? snapshotToken?.sizeCells,
    initiative: Number.isFinite(Number(runtimeOverlay?.initiative ?? snapshotToken?.initiative))
      ? Number(runtimeOverlay?.initiative ?? snapshotToken?.initiative)
      : (runtimeOverlay?.initiative ?? snapshotToken?.initiative ?? null),
    ac: Number.isFinite(Number(runtimeOverlay?.ac ?? snapshotToken?.ac))
      ? Number(runtimeOverlay?.ac ?? snapshotToken?.ac)
      : (runtimeOverlay?.ac ?? snapshotToken?.ac ?? null),
    layer: normalizeSnapshotLayerId(visualState?.layer || snapshotToken?.layer)
  };

  const mergedEntityData = mergeEmbeddedTokenEntityData(
    richEntityData || snapshotToken?.entityPayload,
    canonicalTemplateEntity
  );

  if (!mergedEntityData) {
    return {
      token: normalizedToken,
      mergedEntityData: null
    };
  }

  const normalizedType = getNormalizedEntityType(normalizedToken, mergedEntityData);
  if (normalizedType === 'monster') {
    normalizedToken.monsterData = mergedEntityData;
  } else {
    normalizedToken.characterData = mergedEntityData;
  }

  if (mergedEntityData?.spells && Object.keys(mergedEntityData.spells).length > 0) {
    normalizedToken.spells = mergedEntityData.spells;
  }
  if (mergedEntityData?.classFeatures && mergedEntityData.classFeatures.length > 0) {
    normalizedToken.classFeatures = mergedEntityData.classFeatures;
  }
  if (mergedEntityData?.items && mergedEntityData.items.length > 0) {
    normalizedToken.items = mergedEntityData.items;
  }
  if (mergedEntityData?.feats && mergedEntityData.feats.length > 0) {
    normalizedToken.feats = mergedEntityData.feats;
  }
  if (mergedEntityData?.actions && Object.keys(mergedEntityData.actions).length > 0) {
    normalizedToken.actions = mergedEntityData.actions;
  }

  if (normalizedType === 'monster') {
    if (mergedEntityData?.actions && Array.isArray(mergedEntityData.actions)) {
      normalizedToken.actions = mergedEntityData.actions;
    }
    if (mergedEntityData?.legendaryActions && Array.isArray(mergedEntityData.legendaryActions)) {
      normalizedToken.legendaryActions = mergedEntityData.legendaryActions;
    }
    if (mergedEntityData?.specialAbilities && Array.isArray(mergedEntityData.specialAbilities)) {
      normalizedToken.specialAbilities = mergedEntityData.specialAbilities;
    }
    normalizedToken.monsterData = mergedEntityData;
  }

  return {
    token: rebuildRestoredTokenProjection(normalizedToken, mergedEntityData),
    mergedEntityData
  };
};

export const restoreSnapshotToken = ({
  snapshotToken,
  templateMap = {},
  canonicalCharacterEntities = {},
  canonicalMonsterEntities = {}
}) => {
  const entityType = normalizeSnapshotEntityType(snapshotToken?.type || snapshotToken?.entityRef?.entityType);
  const localEntityMap = entityType === 'monster'
    ? canonicalMonsterEntities
    : canonicalCharacterEntities;
  const embeddedEntityData = snapshotToken?.entityPayload
    // Resolve the instance-specific reference before any source-template key.
    // date_of_change: 2026-09-05
    || templateMap?.[snapshotToken?.monsterDataRef || snapshotToken?.characterDataRef]
    || templateMap?.[snapshotToken?.entityRef?.entityId]
    || templateMap?.[snapshotToken?.baseTemplate]
    || null;
  const templateLookupCandidates = getSessionTemplateLookupCandidates(snapshotToken, embeddedEntityData);
  const canonicalTemplateEntity = templateLookupCandidates.reduce((foundEntity, candidate) => (
    foundEntity || localEntityMap?.[candidate] || null
  ), null);

  return rehydrateSavedToken({
    token: snapshotToken,
    richEntityData: embeddedEntityData,
    canonicalTemplateEntity
  });
};

export const restoreSnapshotTokenCollection = ({
  tokens = [],
  templateMap = {},
  canonicalCharacterEntities = {},
  canonicalMonsterEntities = {}
}) => (
  (Array.isArray(tokens) ? tokens : []).map((snapshotToken) => (
    restoreSnapshotToken({
      snapshotToken,
      templateMap,
      canonicalCharacterEntities,
      canonicalMonsterEntities
    }).token
  ))
);

export default rehydrateSavedToken;
